import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, Target, BookHeart, Calendar, Trophy, CheckCircle2, Circle, Shield } from "lucide-react";

export default function MemberSpacePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${apiBase}/api/health-pass/public/${token}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(async (r) => {
        const detail = await r?.json?.().catch(() => null);
        setError(detail?.detail || "Lien d'espace membre invalide ou expiré.");
      });
  }, [token, apiBase]);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Shield size={48} className="mx-auto mb-4 text-impact-red"/>
          <h1 className="font-display font-black text-3xl mb-3">LIEN INVALIDE</h1>
          <p className="text-white/70 text-sm">{error}</p>
          <p className="text-white/50 text-xs mt-4">Demande à ton coach un nouveau lien.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground bg-black text-white">Chargement…</div>;
  }

  const mv = data.member_view || {};
  const pct = mv.progression_pct || 0;
  const isMember = data.variant === "member_view";

  if (!isMember) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 text-center">
        <div>
          <Shield size={48} className="mx-auto mb-4 text-impact-gold"/>
          <p>Ce lien n'est pas un espace membre. <a href={`/pass/${token}`} className="underline text-impact-gold">Voir le passeport →</a></p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-impact-gold/40 px-6 py-6 bg-gradient-to-r from-black via-zinc-900 to-black">
        <div className="ticker text-impact-gold mb-2">— MON ESPACE MEMBRE</div>
        <h1 className="font-display font-black text-4xl">{data.full_name}</h1>
        <div className="flex flex-wrap gap-2 mt-3 text-xs">
          <span className="bg-impact-gold/15 text-impact-gold px-3 py-1 ticker">{data.discipline?.replace(/_/g, " ").toUpperCase()}</span>
          <span className="bg-white/10 px-3 py-1 ticker">{data.issued_by_dojo}</span>
        </div>
      </header>

      {/* ANNONCE PETITS SAMOURAÏS */}
      <a href="/infos-petits-samourais" className="block px-6 py-4 border-b border-white/10 bg-amber-950/20 hover:bg-amber-950/40 transition-colors" data-testid="ms-petits-samourais-banner">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🥋</span>
          <div className="flex-1">
            <div className="text-impact-gold text-sm font-semibold">Cours Petits Samouraïs — Nouvelle formule</div>
            <div className="text-zinc-400 text-xs">Autodéfense Jiu-jitsu Can Ryu (3–5 ans) · transition Junior à 6 ans · infos été. Cliquez pour les détails →</div>
          </div>
        </div>
      </a>

      {/* ANNONCE ÉVÉNEMENTS */}
      <a href="/evenements" className="block px-6 py-4 border-b border-white/10 bg-[#D90429]/10 hover:bg-[#D90429]/20 transition-colors" data-testid="ms-evenements-banner">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🗓️</span>
          <div className="flex-1">
            <div className="text-[#ff6b7f] text-sm font-semibold">Événements & compétitions</div>
            <div className="text-zinc-400 text-xs">Séminaires, compétitions et stages du dojo — participe avec nous. Cliquez pour voir et t'inscrire →</div>
          </div>
        </div>
      </a>

      <section className="px-6 py-8 border-b border-white/10" data-testid="ms-progression">
        <div className="flex items-center gap-2 mb-4">
          <Target size={20} className="text-impact-gold"/>
          <h2 className="ticker text-impact-gold">MA PROGRESSION</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          {mv.current_belt ? (
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full" style={{ background: mv.current_belt.couleur_hex, boxShadow: "inset 0 0 0 1px #ffffff44" }}/>
              <span className="font-display font-bold text-xl">{mv.current_belt.nom}</span>
            </div>
          ) : <span className="text-sm text-white/50 italic">Pas encore évalué</span>}
          {mv.next_belt && (
            <>
              <span className="text-white/40 text-2xl">→</span>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full opacity-50" style={{ background: mv.next_belt.couleur_hex, boxShadow: "inset 0 0 0 1px #ffffff44" }}/>
                <span className="text-white/70 text-lg">{mv.next_belt.nom}</span>
              </div>
            </>
          )}
        </div>
        {mv.next_belt && (
          <>
            <div className="h-4 bg-white/10 relative overflow-hidden">
              <div className="bg-impact-gold h-full transition-all" style={{ width: `${pct}%` }} data-testid="ms-progression-bar"/>
            </div>
            <div className="flex justify-between text-xs mt-2">
              <span className="text-white/60">{mv.techniques_mastered?.length || 0} / {(mv.techniques_mastered?.length || 0) + (mv.techniques_remaining?.length || 0)} techniques maîtrisées</span>
              <span className="font-bold text-impact-gold" data-testid="ms-pct">{pct}%</span>
            </div>
          </>
        )}
        {!mv.next_belt && mv.current_belt && (
          <div className="mt-2 text-impact-gold font-bold flex items-center gap-2"><Trophy size={20}/> Plus haute ceinture atteinte !</div>
        )}
      </section>

      {/* TECHNIQUES */}
      <section className="px-6 py-8 border-b border-white/10" data-testid="ms-techniques">
        <h2 className="ticker text-impact-gold mb-4">TECHNIQUES À VALIDER POUR LA PROCHAINE CEINTURE</h2>
        {(mv.techniques_remaining?.length || 0) === 0 && (mv.techniques_mastered?.length || 0) === 0 ? (
          <p className="text-sm text-white/60 italic">Aucune technique requise pour le moment.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {mv.techniques_mastered?.map((t) => (
              <div key={t.id} className="flex items-center gap-3 border border-impact-gold/40 bg-impact-gold/5 px-3 py-2">
                <CheckCircle2 size={18} className="text-impact-gold flex-shrink-0"/>
                <div className="flex-1">
                  <div className="font-medium">{t.nom}</div>
                  <div className="text-xs text-white/50">{t.categorie?.replace(/_/g, " ")}</div>
                </div>
              </div>
            ))}
            {mv.techniques_remaining?.map((t) => (
              <div key={t.id} className="flex items-center gap-3 border border-white/15 px-3 py-2">
                <Circle size={18} className="text-white/40 flex-shrink-0"/>
                <div className="flex-1">
                  <div className="text-white/90">{t.nom}</div>
                  <div className="text-xs text-white/50">{t.categorie?.replace(/_/g, " ")}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CARNET PARTAGÉ */}
      <section className="px-6 py-8 border-b border-white/10" data-testid="ms-carnet">
        <div className="flex items-center gap-2 mb-4">
          <BookHeart size={20} className="text-impact-gold"/>
          <h2 className="ticker text-impact-gold">MON CARNET — NOTES DE MES COACHS</h2>
        </div>
        {!mv.carnet_partage || mv.carnet_partage.length === 0 ? (
          <p className="text-sm text-white/60 italic">Aucune note partagée pour l'instant. Tes coachs partageront ici tes objectifs et observations.</p>
        ) : (
          <div className="space-y-3">
            {mv.carnet_partage.map((e) => (
              <article key={e.id} className="border-l-2 border-impact-gold/60 pl-4 py-1">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar size={12} className="text-white/50"/>
                  <span className="text-xs text-white/50">{e.entry_date}</span>
                  <span className="ticker text-[10px] bg-white/10 px-1.5 py-0.5">{e.entry_type?.replace(/_/g, " ")}</span>
                  {e.author_name && <span className="text-xs text-white/50">— {e.author_name}</span>}
                </div>
                <h3 className="font-bold">{e.title}</h3>
                {e.content && <p className="text-sm text-white/80 whitespace-pre-wrap mt-1">{e.content}</p>}
                {e.entry_type === "promotion_ceinture" && e.ceinture_nouvelle && (
                  <div className="mt-2 inline-block bg-impact-gold/15 text-impact-gold font-mono text-sm px-2 py-1">
                    <Award size={12} className="inline mr-1"/> {e.ceinture_precedente || "—"} → {e.ceinture_nouvelle}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      <footer className="px-6 py-8 text-xs text-white/40 text-center">
        Mon Espace Membre · {data.issued_by_dojo} · Lien personnel, ne pas partager.
      </footer>
    </div>
  );
}
